import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { CHAT_QUERY_KEYS, CHAT_CONFIG } from "../configs/chat.config";
import { ChatService } from "../services/chat.service";
import type { ChatUsersResponse } from "../types/chat.types";

export function useChatRooms({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: CHAT_QUERY_KEYS.rooms(),
    queryFn: () => ChatService.getRooms(),
    select: (data) => data.rooms,
    refetchInterval: CHAT_CONFIG.ROOMS_REFETCH_INTERVAL_MS,
    staleTime: 0,
    enabled,
  });
}

export function useChatRoom(roomId: string | null) {
  return useQuery({
    queryKey: CHAT_QUERY_KEYS.room(roomId ?? ""),
    queryFn: () => ChatService.getRoom(roomId ?? ""),
    select: (data) => data.room,
    enabled: !!roomId,
  });
}

export function useChatMessages(roomId: string | null) {
  return useQuery({
    queryKey: CHAT_QUERY_KEYS.messages(roomId ?? ""),
    queryFn: () => ChatService.getMessages(roomId ?? "", CHAT_CONFIG.MESSAGES_LIMIT),
    select: (data) => ({
      messages: data.messages,
      lastReadAt: data.last_read_at,
    }),
    enabled: !!roomId,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useChatUsers({ q = "" }: { q?: string } = {}) {
  return useInfiniteQuery({
    queryKey: [...CHAT_QUERY_KEYS.users(), { q }],
    queryFn: ({ pageParam }) => ChatService.getUsers({ q, page: pageParam }),
    initialPageParam: 1,
    // Advance to next page as long as either group still has pages left.
    // The API paginates representatives and employees independently but
    // with a shared `page` param, so we track the max current_page.
    getNextPageParam: (lastPage: ChatUsersResponse) => {
      const metaR = lastPage.metadata?.representatives;
      const metaE = lastPage.metadata?.employees;
      if (!metaR && !metaE) return undefined;
      const hasMoreR = metaR ? metaR.current_page < metaR.total_pages : false;
      const hasMoreE = metaE ? metaE.current_page < metaE.total_pages : false;
      if (!hasMoreR && !hasMoreE) return undefined;
      const current = Math.max(metaR?.current_page ?? 1, metaE?.current_page ?? 1);
      return current + 1;
    },
    select: (data) => {
      const lastPage = data.pages[data.pages.length - 1];
      const metaR = lastPage?.metadata?.representatives;
      const metaE = lastPage?.metadata?.employees;
      return {
        representatives: data.pages.flatMap((p) => p.representatives),
        employees: data.pages.flatMap((p) => p.employees),
        metadata: data.pages[0]?.metadata,
        hasMoreRepresentatives: metaR ? metaR.current_page < metaR.total_pages : false,
        hasMoreEmployees: metaE ? metaE.current_page < metaE.total_pages : false,
      };
    },
    // Keep the previous results visible while the debounced search
    // query is mid-flight to avoid UI flicker.
    placeholderData: (previous) => previous,
  });
}
